import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X } from "lucide-react";
import styles from "./SearchBar.module.css";

// ── Public-only search data (all portal dashboard pages excluded) ─────────────
// Each entry: label (shown), path (navigate to), category (badge), keywords (for matching)
const SEARCH_DATA = [
  // ── Main discovery pages ───────────────────────────────────────────────────
  { label: "Home",                    path: "/",                            category: "Page",     keywords: ["homepage", "main", "landing", "start", "kooulu"] },
  { label: "About Us",                path: "/aboutus",                     category: "Page",     keywords: ["about", "company", "who we are", "our story", "team", "mission"] },
  { label: "Activities",              path: "/activities",                  category: "Page",     keywords: ["camps", "programs", "kids activities", "children", "listing", "extracurricular"] },
  { label: "Events",                  path: "/events",                      category: "Page",     keywords: ["shows", "concerts", "community events", "upcoming", "local events"] },
  { label: "Classes",                 path: "/classes",                     category: "Page",     keywords: ["lessons", "courses", "learning", "education", "skills"] },
  { label: "Blogs",                   path: "/blogs",                       category: "Page",     keywords: ["blog", "articles", "news", "posts", "read", "stories", "tips"] },
  { label: "Schools",                 path: "/schools",                     category: "Page",     keywords: ["school", "partners", "facilities", "centers", "institutions", "listing"] },
  { label: "Tutors",                  path: "/tutors/all",                  category: "Page",     keywords: ["tutors", "teachers", "instructors", "educators", "coaching", "find tutor"] },
  { label: "Childcare Facilities",    path: "/childcare-facility-listing",  category: "Page",     keywords: ["childcare", "daycare", "nursery", "preschool", "facilities", "find childcare"] },
  { label: "FAQ",                     path: "/faq",                         category: "Page",     keywords: ["faq", "frequently asked questions", "help", "answers", "support"] },
  { label: "Contact Us",              path: "/contactus",                   category: "Page",     keywords: ["contact", "reach us", "get in touch", "email", "phone", "message", "support"] },
  { label: "How It Works",            path: "/how-it-works",                category: "Page",     keywords: ["how it works", "guide", "steps", "process", "get started", "explained"] },
  { label: "RSS Feed",                path: "/rssfeed",                     category: "Page",     keywords: ["rss", "feed", "syndication", "subscribe", "updates", "news feed"] },
  { label: "Advertising",             path: "/advertising-subscriptions",   category: "Page",     keywords: ["advertise", "promote", "subscription", "plans", "list business", "sponsor"] },
  { label: "Cart",                    path: "/cart",                        category: "Page",     keywords: ["cart", "checkout", "order", "purchase", "buy", "basket"] },

  // ── Login pages ────────────────────────────────────────────────────────────
  { label: "Parent Login",            path: "/Parentlogin",                 category: "Login",    keywords: ["parent sign in", "parent account", "log in as parent"] },
  { label: "Partner Login",           path: "/partner/login",               category: "Login",    keywords: ["partner sign in", "school login", "provider login", "organization login"] },
  { label: "Tutor Login",             path: "/tutor/login",                 category: "Login",    keywords: ["tutor sign in", "instructor login", "teacher login"] },
  { label: "Childcare Login",         path: "/childcare/login",             category: "Login",    keywords: ["childcare sign in", "facility login", "daycare login", "center login"] },

  // ── Registration pages ─────────────────────────────────────────────────────
  { label: "Parent Registration",     path: "/parent-registration",         category: "Register", keywords: ["parent sign up", "create parent account", "join as parent", "new parent"] },
  { label: "Partner Registration",    path: "/partner-registration",        category: "Register", keywords: ["school registration", "provider signup", "list your school", "become partner"] },
  { label: "Tutor Registration",      path: "/tutor-registration",          category: "Register", keywords: ["become a tutor", "tutor signup", "instructor registration", "teach on kooulu"] },
  { label: "Childcare Registration",  path: "/childcare/registration",      category: "Register", keywords: ["facility registration", "daycare signup", "register facility", "childcare onboarding"] },

  // ── Legal & policy pages ───────────────────────────────────────────────────
  { label: "Privacy Policy",          path: "/privacy-policy",              category: "Legal",    keywords: ["privacy", "data protection", "gdpr", "cookies", "personal information", "data policy"] },
  { label: "Terms & Conditions",      path: "/terms-and-conditions",        category: "Legal",    keywords: ["terms", "conditions", "legal", "agreement", "rules", "tos", "terms of service"] },
  { label: "Refund Policy",           path: "/refund-and-cancellation",     category: "Legal",    keywords: ["refund", "cancellation", "return policy", "money back", "cancel"] },
];

const CATEGORY_STYLE = {
  Page:     { bg: "#eff6ff", color: "#1d4ed8" },
  Login:    { bg: "#f0fdf4", color: "#166534" },
  Register: { bg: "#fdf4ff", color: "#7e22ce" },
  Legal:    { bg: "#fff7ed", color: "#9a3412" },
};

// Returns JSX with the matched portion wrapped in <mark>
function highlightMatch(text, query) {
  if (!query) return text;
  const idx = text.toLowerCase().indexOf(query.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className={styles.highlight}>{text.slice(idx, idx + query.length)}</mark>
      {text.slice(idx + query.length)}
    </>
  );
}

// Two words "match" if one is a substring of the other, or they share a stem.
// Handles plurals like activity→activities, facility→facilities (y→ies endings).
function wordMatches(queryWord, labelWord) {
  if (labelWord.includes(queryWord)) return true; // "events".includes("event") ✓
  if (queryWord.includes(labelWord)) return true; // query longer than label word
  // Strip last char of query and check if label word starts with that stem.
  // Catches y→ies: "activit" is a prefix of "activities".
  if (queryWord.length >= 4 && labelWord.startsWith(queryWord.slice(0, -1))) return true;
  return false;
}

// Score an item against a query. Higher = better match.
// Ranking tiers: exact (100) > starts-with (80) > full-phrase (60)
//   > all words match (50) > some words match (40+) > keyword matches (10-35)
function scoreItem(item, q, words) {
  const label      = item.label.toLowerCase();
  const labelWords = label.split(/\s+/);

  // Tier 1 — full phrase against label
  if (label === q)          return 100;
  if (label.startsWith(q))  return 80;
  if (label.includes(q))    return 60;

  // Tier 2 — word-level match against label (handles plurals/variations)
  const labelWordHits = words.filter((w) =>
    labelWords.some((lw) => wordMatches(w, lw)),
  ).length;
  if (labelWordHits === words.length) return 50;
  if (labelWordHits > 0)              return 40 + labelWordHits * 3;

  // Tier 3 — keyword phrase match
  if (item.keywords.some((kw) => kw === q))       return 35;
  if (item.keywords.some((kw) => kw.includes(q))) return 25;

  // Tier 4 — word-level match against keywords
  const kwWordHits = words.filter((w) =>
    item.keywords.some((kw) =>
      kw.split(/\s+/).some((kw2) => wordMatches(w, kw2)),
    ),
  ).length;
  if (kwWordHits === words.length) return 20;
  if (kwWordHits > 0)              return 10 + kwWordHits * 2;

  return 0;
}

function runSearch(query) {
  if (!query.trim()) return [];
  const q     = query.toLowerCase().trim();
  // Split into words; skip single chars to avoid overly broad matches
  const words = q.split(/\s+/).filter((w) => w.length > 1);
  const matchWords = words.length > 0 ? words : [q];

  return SEARCH_DATA
    .map((item) => ({ item, score: scoreItem(item, q, matchWords) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .map(({ item }) => item)
    .slice(0, 12);
}

// ── Component ─────────────────────────────────────────────────────────────────
const SearchBar = ({ iconOnly = false }) => {
  const navigate = useNavigate();
  const [query, setQuery]             = useState("");
  const [results, setResults]         = useState([]);
  const [isOpen, setIsOpen]           = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [mobileExpanded, setMobileExpanded] = useState(false);

  const inputRef     = useRef(null);
  const containerRef = useRef(null);
  const debounceRef  = useRef(null);

  // ── Debounced search ──────────────────────────────────────────────────────
  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }
    debounceRef.current = setTimeout(() => {
      const found = runSearch(query);
      setResults(found);
      setIsOpen(found.length > 0);
      setActiveIndex(-1);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  // ── Click outside to close ────────────────────────────────────────────────
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setMobileExpanded(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const closeAndNavigate = useCallback(
    (path) => {
      navigate(path);
      setQuery("");
      setResults([]);
      setIsOpen(false);
      setActiveIndex(-1);
      setMobileExpanded(false);
    },
    [navigate],
  );

  const clear = () => {
    setQuery("");
    setIsOpen(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  // ── Keyboard navigation ───────────────────────────────────────────────────
  const handleKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => (isOpen ? Math.min(i + 1, results.length - 1) : i));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      if (activeIndex >= 0 && results[activeIndex]) {
        e.preventDefault();
        closeAndNavigate(results[activeIndex].path);
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setQuery("");
      setMobileExpanded(false);
      inputRef.current?.blur();
    }
  };

  const handleMobileToggle = () => {
    setMobileExpanded((v) => !v);
    if (!mobileExpanded) {
      setTimeout(() => inputRef.current?.focus(), 60);
    } else {
      setQuery("");
      setIsOpen(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div
      ref={containerRef}
      className={`${styles.wrapper} ${mobileExpanded ? styles.mobileExpanded : ""} ${iconOnly ? styles.iconOnly : ""}`}
    >
      {/* Mobile icon — visible on ≤768px */}
      <button
        className={styles.mobileIcon}
        onClick={handleMobileToggle}
        aria-label={mobileExpanded ? "Close search" : "Open search"}
      >
        {mobileExpanded ? <X size={18} /> : <Search size={18} />}
      </button>

      {/* Desktop / expanded-mobile input */}
      <div className={styles.inputWrapper}>
        <Search size={14} className={styles.searchIconLeft} aria-hidden="true" />
        <input
          ref={inputRef}
          type="text"
          className={styles.input}
          placeholder="Search pages, features..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (query && results.length > 0) setIsOpen(true);
          }}
          role="combobox"
          aria-label="Search"
          aria-expanded={isOpen}
          aria-controls="search-results-listbox"
          aria-haspopup="listbox"
          aria-autocomplete="list"
          autoComplete="off"
          spellCheck={false}
        />
        {query && (
          <button
            className={styles.clearBtn}
            onClick={clear}
            aria-label="Clear search"
            tabIndex={-1}
          >
            <X size={13} />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && results.length > 0 && (
        <ul id="search-results-listbox" className={styles.dropdown} role="listbox" aria-label="Search results">
          {results.map((item, idx) => {
            const catStyle = CATEGORY_STYLE[item.category] || CATEGORY_STYLE.Page;
            return (
              <li
                key={`${item.path}-${idx}`}
                className={`${styles.result} ${idx === activeIndex ? styles.active : ""}`}
                role="option"
                aria-selected={idx === activeIndex}
                onMouseDown={(e) => {
                  e.preventDefault(); // prevent input blur before navigate
                  closeAndNavigate(item.path);
                }}
                onMouseEnter={() => setActiveIndex(idx)}
              >
                <Search size={13} className={styles.resultIcon} aria-hidden="true" />
                <span className={styles.resultLabel}>
                  {highlightMatch(item.label, query)}
                </span>
                <span
                  className={styles.categoryBadge}
                  style={{ background: catStyle.bg, color: catStyle.color }}
                >
                  {item.category}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SearchBar;
